import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Upload,
  GetApp,
  ExpandMore,
  Warning
} from '@mui/icons-material';
import PartForm from './PartForm';

/**
 * PartsCatalogDialogs Component
 * Contains all dialog components: Quick Add, Full Add/Edit (PartForm), Documents, and Dependencies
 */

// Quick Add Dialog Component
export const QuickAddDialog = ({ open, onClose, formData, setFormData, onSubmit }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Quick Add Part</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Part Number"
              value={formData.partNumber}
              onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Part Name"
              value={formData.partName ?? ''}
              onChange={(e) => setFormData({ ...formData, partName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSubmit(true)} variant="contained">
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Part Documents Dialog Component
export const PartDocumentsDialog = ({
  open,
  onClose,
  selectedPartForDocuments,
  documentForm,
  setDocumentForm,
  documentUploading,
  documentUploadProgress,
  documentList,
  onUpload,
  onDownload
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {selectedPartForDocuments
          ? `Documents for Part ${selectedPartForDocuments.partNumber}`
          : 'Part Documents'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Existing Documents
          </Typography>
          {documentList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No documents uploaded yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Sub-Type</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Uploaded At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentList.map((doc) => (
                  <TableRow key={doc._id}>
                    <TableCell>{doc.documentType}</TableCell>
                    <TableCell>{doc.documentSubType || '-'}</TableCell>
                    <TableCell>{doc.version}</TableCell>
                    <TableCell>{doc.description || '-'}</TableCell>
                    <TableCell>
                      {doc.uploadedBy
                        ? `${doc.uploadedBy.firstName || ''} ${doc.uploadedBy.lastName || ''}`.trim() ||
                          doc.uploadedBy.email
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => onDownload(doc._id, doc.originalFileName)}
                        title="Download Document"
                      >
                        <GetApp />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Upload New Document
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="Document Type"
                value={documentForm.documentType}
                onChange={(e) =>
                  setDocumentForm({ ...documentForm, documentType: e.target.value })
                }
              >
                <MenuItem value="MANUAL">Manual</MenuItem>
                <MenuItem value="SPECIFICATIONS">Specifications</MenuItem>
                <MenuItem value="WARRANTY">Warranty</MenuItem>
                <MenuItem value="SERVICE_HISTORY">Service History</MenuItem>
                <MenuItem value="CERTIFICATE_OF_REGISTRATION">Certificate of Registration</MenuItem>
                <MenuItem value="CERTIFICATE_OF_AIRWORTHINESS">Certificate of Airworthiness</MenuItem>
                <MenuItem value="INSURANCE">Insurance</MenuItem>
                <MenuItem value="TYPE_CERTIFICATE">Type Certificate</MenuItem>
                <MenuItem value="OPERATOR_PERMIT">Operator Permit</MenuItem>
                <MenuItem value="IMPORT_DOCUMENTS">Import Documents</MenuItem>
                <MenuItem value="MODIFICATIONS">Modifications</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Document Sub-Type (Optional)"
                value={documentForm.documentSubType}
                onChange={(e) =>
                  setDocumentForm({ ...documentForm, documentSubType: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (Optional)"
                value={documentForm.description}
                onChange={(e) =>
                  setDocumentForm({ ...documentForm, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<Upload />}
              >
                {documentForm.file ? documentForm.file.name : 'Select File'}
                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setDocumentForm({
                      ...documentForm,
                      file: e.target.files && e.target.files[0] ? e.target.files[0] : null
                    })
                  }
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4,.mov,.txt"
                />
              </Button>
              {documentUploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={documentUploadProgress} />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    Uploading... {documentUploadProgress}%
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={onUpload}
          variant="contained"
          disabled={documentUploading || !documentForm.file || !documentForm.documentType}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Serial number dependencies confirmation (before removing a serial from part)
export const SerialNumberDependenciesDialog = ({
  open,
  onClose,
  serialNumber,
  partNumber,
  dependencies,
  onConfirm
}) => {
  const dep = dependencies || {};
  const hasAny =
    (dep.components && dep.components.length > 0) ||
    (dep.inventoryItems && dep.inventoryItems.length > 0) ||
    (dep.receivingRecords && dep.receivingRecords.length > 0) ||
    (dep.issuanceRecords && dep.issuanceRecords.length > 0) ||
    (dep.equipmentCategoryFields && dep.equipmentCategoryFields.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6">Serial number in use</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {hasAny ? (
              <>
                Serial <strong>{serialNumber || '—'}</strong> for part <strong>{partNumber || '—'}</strong> is
                referenced below. Removing it from the part catalog will not change these records. Do you want to
                remove it anyway?
              </>
            ) : (
              <>
                This will remove all serial numbers from this part
                {serialNumber && (
                  <> (e.g. <strong>{serialNumber}</strong>)</>
                )}
                . Do you want to continue?
              </>
            )}
          </Typography>
        </Alert>
        {hasAny && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
              Dependencies:
            </Typography>
            {dep.components && dep.components.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography><strong>Components ({dep.components.length})</strong></Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dep.components.map((c, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Serial: ${c.serialNumber || 'N/A'} | Position: ${c.position || 'N/A'}`}
                          secondary={`Aircraft: ${c.aircraftRegistration || 'N/A'} | Assembly: ${c.assemblySerialNumber || 'N/A'} (${c.assemblyTypeName || 'N/A'})`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
            {dep.inventoryItems && dep.inventoryItems.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography><strong>Store – Inventory ({dep.inventoryItems.length})</strong></Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dep.inventoryItems.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`${item.partNumber} | Serial: ${item.serialNumber || 'N/A'}`}
                          secondary={`Qty: ${item.quantity} | Status: ${item.status || 'N/A'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
            {dep.receivingRecords && dep.receivingRecords.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography><strong>Store – Receiving ({dep.receivingRecords.length})</strong></Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dep.receivingRecords.map((r, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={r.receivingDate ? new Date(r.receivingDate).toLocaleDateString() : '—'}
                          secondary={r.itemSummary || ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
            {dep.issuanceRecords && dep.issuanceRecords.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography><strong>Store – Issuance ({dep.issuanceRecords.length})</strong></Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dep.issuanceRecords.map((iss, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={iss.issuedDate ? new Date(iss.issuedDate).toLocaleDateString() : '—'}
                          secondary={iss.itemSummary || ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
            {dep.equipmentCategoryFields && dep.equipmentCategoryFields.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography><strong>Equipment category fields ({dep.equipmentCategoryFields.length})</strong></Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dep.equipmentCategoryFields.map((ef, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={`Serial: ${ef.serialNumber || 'N/A'}`} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            onConfirm && onConfirm();
            onClose();
          }}
          color="primary"
          variant="contained"
        >
          Remove anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dependency Details Dialog Component
export const DependencyDetailsDialog = ({
  open,
  onClose,
  dependencyData,
  partToDelete
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="error" />
          <Typography variant="h6">Cannot Delete Part</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {dependencyData && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Part Number: {partToDelete?.partNumber}</strong>
              </Typography>
              <Typography variant="body2">
                This part cannot be deleted because it has dependencies in the system. 
                Please remove or update the dependencies before deleting this part.
              </Typography>
            </Alert>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
              Dependency Summary:
            </Typography>
            <Box sx={{ mb: 2 }}>
              {dependencyData.summary && (
                <Grid container spacing={1}>
                  {Object.entries(dependencyData.summary).map(([key, count]) => {
                    if (count === 0) return null;
                    const label = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .trim();
                    return (
                      <Grid item xs={6} sm={4} key={key}>
                        <Chip
                          label={`${label}: ${count}`}
                          color="error"
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Detailed Dependencies:
            </Typography>

            {/* Components */}
            {dependencyData.dependencies?.components?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Components ({dependencyData.dependencies.components.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.components.map((comp, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Serial: ${comp.serialNumber || 'N/A'} | Position: ${comp.position || 'N/A'}`}
                          secondary={
                            <>
                              Aircraft: {comp.aircraftRegistration || 'N/A'} | 
                              Assembly: {comp.assemblySerialNumber || 'N/A'} | 
                              Status: {comp.isActive ? 'Active' : 'Archived'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Part Alternates */}
            {dependencyData.dependencies?.partAlternates?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Part Alternates ({dependencyData.dependencies.partAlternates.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.partAlternates.map((alt, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Type: ${alt.partType}`}
                          secondary={
                            <>
                              Related Part: {alt.relatedPartNumber || 'N/A'} - {alt.relatedPartDescription || 'N/A'} | 
                              Status: {alt.isActive ? 'Active' : 'Archived'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Inventory Items */}
            {dependencyData.dependencies?.inventoryItems?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Inventory Items ({dependencyData.dependencies.inventoryItems.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.inventoryItems.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Quantity: ${item.quantity || 0} | Status: ${item.status || 'N/A'}`}
                          secondary={
                            <>
                              Location: {item.location?.warehouse || 'N/A'} - {item.location?.shelf || 'N/A'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Work Orders */}
            {dependencyData.dependencies?.workOrders?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Work Orders ({dependencyData.dependencies.workOrders.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.workOrders.map((wo, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Work Order: ${wo.workOrderNumber || 'N/A'} | Status: ${wo.status || 'N/A'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Receiving Records */}
            {dependencyData.dependencies?.receivingRecords?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Receiving Records ({dependencyData.dependencies.receivingRecords.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.receivingRecords.map((rec, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Receiving #: ${rec.receivingNumber || 'N/A'} | Quantity: ${rec.quantity || 0}`}
                          secondary={
                            <>
                              Date: {rec.receivedDate ? new Date(rec.receivedDate).toLocaleDateString() : 'N/A'} | 
                              Status: {rec.status || 'N/A'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Issuance Records */}
            {dependencyData.dependencies?.issuanceRecords?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Issuance Records ({dependencyData.dependencies.issuanceRecords.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.issuanceRecords.map((iss, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Issuance #: ${iss.issuanceNumber || 'N/A'} | Quantity: ${iss.quantity || 0}`}
                          secondary={
                            <>
                              Date: {iss.issuedDate ? new Date(iss.issuedDate).toLocaleDateString() : 'N/A'} | 
                              Status: {iss.status || 'N/A'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Reconciliation Records */}
            {dependencyData.dependencies?.reconciliationRecords?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Reconciliation Records ({dependencyData.dependencies.reconciliationRecords.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.reconciliationRecords.map((rec, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Reconciliation #: ${rec.reconciliationNumber || 'N/A'} | Quantity: ${rec.quantity || 0}`}
                          secondary={
                            <>
                              Date: {rec.reconciledDate ? new Date(rec.reconciledDate).toLocaleDateString() : 'N/A'} | 
                              Status: {rec.status || 'N/A'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Reorder Requests */}
            {dependencyData.dependencies?.reorderRequests?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Reorder Requests ({dependencyData.dependencies.reorderRequests.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.reorderRequests.map((req, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Request #: ${req.requestNumber || 'N/A'} | Quantity: ${req.quantity || 0}`}
                          secondary={
                            <>
                              Date: {req.requestedDate ? new Date(req.requestedDate).toLocaleDateString() : 'N/A'} | 
                              Status: {req.status || 'N/A'}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Category Specific Fields */}
            {dependencyData.dependencies?.categorySpecificFields?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>Category Specific Fields ({dependencyData.dependencies.categorySpecificFields.length})</strong>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {dependencyData.dependencies.categorySpecificFields.map((field, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Type: ${field.type} | Category: ${field.category || 'N/A'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Full Add/Edit Dialog wrapper (uses PartForm component)
export const PartFormDialog = ({
  open,
  onClose,
  selectedPart,
  formData,
  setFormData,
  formTab,
  setFormTab,
  dropdownOptions,
  displayTimezone,
  onSubmit,
  onDelete,
  user,
  onRefreshDropdowns,
  onOpenSerialDependencies
}) => {
  return (
    <PartForm
      open={open}
      onClose={onClose}
      selectedPart={selectedPart}
      formData={formData}
      setFormData={setFormData}
      formTab={formTab}
      setFormTab={setFormTab}
      dropdownOptions={dropdownOptions}
      displayTimezone={displayTimezone}
      onSubmit={onSubmit}
      onDelete={onDelete}
      user={user}
      onRefreshDropdowns={onRefreshDropdowns}
      onOpenSerialDependencies={onOpenSerialDependencies}
    />
  );
};
